import { useCallback, useContext, useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import styles from "./RenderSelectedProduct.module.css";
import { Button, Checkbox, SkeletonBodyText, TextField } from "@shopify/polaris";
import { ProductObj, VariantsObj } from "../../apps/Bundles/Types";
import { BundleTypes, getProductsDynamicallyAndReturnNewProductsArray } from "../../apps/Bundles/constants";
import DragHandle from "../Icons/DragHandle";
import CloseIcon from "../Icons/CloseIcon";
import CustomModal from "../CustomModal/CustomModal";
import { AppConfigContext } from "../../context/AppConfigContext";
import { mergeProductsById } from "../../utilities/functions";
import { RenderVariantInfo } from "../../apps/Bundles/BundleBuilder/BundleBuilderBundle/BBProductCard";
import { PlusIcon, ProductIcon, XIcon } from "@shopify/polaris-icons";
import { InlineError, Thumbnail } from "@shopify/polaris";

const RenderSelectedProduct = ({
    bundleLevel = "product",
    dispatchBundleConfig,
    bundleType,
    products,
    customTitle = "",
    updateKey = "products",
    submit,
    excludeProducts = [],
    hideQuantity = false,
    multiple = 10,
    products_type = "",
    MNMSectional = false,
    index = 0,
    showVariantPicker = false,
    showAlternateProducts = false,
}: {
    bundleLevel: "product" | "variant";
    dispatchBundleConfig: any;
    bundleType: string;
    products: any;
    customTitle?: string;
    updateKey: string;
    submit: (event?: React.FormEvent) => Promise<void>;
    isMobile: boolean;
    excludeProducts?: any;
    hideQuantity?: boolean;
    multiple?: number | boolean;
    products_type?: string;
    MNMSectional?: boolean;
    index?: number;
    showVariantPicker?: boolean;
    showAlternateProducts?: boolean;
}) => {
    const [renderArr, setRenderArr] = useState<any[]>([]);
    // const prevBundleLevel = useRef(bundleLevel);

    // useEffect(() => {
    //     if (prevBundleLevel.current !== bundleLevel) {
    //         setRenderArr([]);
    //         dispatchBundleConfig({
    //             payload: [],
    //             key: updateKey,
    //         });

    //         products.onChange([]);
    //         prevBundleLevel.current = bundleLevel;
    //     }
    // }, [bundleLevel, products]);

    const context = useContext(AppConfigContext);
    const { sft } = context;

    useEffect(() => {
        if (products?.value?.length) {
            products?.runValidation();
        }
        setRenderArr(products?.value);

        return () => {
            if (products?.value?.length) {
                products?.runValidation();
            }
        };
    }, [products.value]);

    const moveCard = useCallback(
        (originalIdx: number, newIdx: number) => {
            const temp = [...renderArr];
            const draggedCard = {
                ...temp[originalIdx],
            };
            temp.splice(originalIdx, 1);
            temp.splice(newIdx, 0, draggedCard);
            if (MNMSectional) {
                dispatchBundleConfig({
                    payload: temp,
                    key: `mix_match_sections`,
                    index: index,
                    subKey: updateKey,
                    isArray: true,
                });
            } else {
                dispatchBundleConfig({
                    payload: temp,
                    key: [updateKey],
                });
            }
            setRenderArr(temp);
            products.onChange(temp);
        },
        [renderArr, products],
    );

    const removeProduct = (pId: string = "", vId: string = "") => {
        const temp: ProductObj[] = [...renderArr];
        let idx: number | undefined = -1;
        if (bundleLevel == "variant") {
            idx = temp.findIndex((p: ProductObj) => p.variants[0]?.id == vId);
        } else {
            idx = temp.findIndex((p) => p.id == pId);
        }
        if (idx >= 0) {
            temp.splice(idx, 1);

            if (MNMSectional) {
                dispatchBundleConfig({
                    payload: temp,
                    key: `mix_match_sections`,
                    index: index,
                    subKey: updateKey,
                    isArray: true,
                });
            } else {
                dispatchBundleConfig({
                    payload: temp,
                    key: [updateKey],
                });
            }
            products.onChange(temp);
        }
    };
    const handleQuantity = (pId = "", vId = "", val: number) => {
        const temp: ProductObj[] = [...renderArr];
        let idx: number | undefined = -1;

        const quantity = val <= 0 ? 1 : val;
        typeof quantity;
        if (bundleLevel == "variant") {
            idx = temp.findIndex((p: ProductObj) => p.variants[0]?.id == vId);
        } else {
            idx = temp.findIndex((p) => p.id == pId);
        }

        if (idx >= 0) {
            temp[idx] = {
                ...temp[idx],
                quantity: val,
            };
        }

        if (MNMSectional) {
            dispatchBundleConfig({
                payload: temp,
                key: `mix_match_sections`,
                index: index,
                subKey: updateKey,
                isArray: true,
            });
        } else {
            dispatchBundleConfig({
                payload: temp,
                key: [updateKey],
            });
        }
        products.onChange(temp);
    };

    const [isFetchingRequest, setIsFetchingRequest] = useState(false);

    const handleProductSearch = async (initialQuery: string = "") => {
        let preSelected = [];
        if (bundleType === BundleTypes.STANDARD && bundleLevel === "variant") {
            const grouped = mergeProductsById(products?.value || []);

            preSelected = grouped.map((product) => ({
                id: product.id,
                variants:
                    product.variants?.map((variant) => ({
                        id: variant.id,
                    })) || [],
            }));
        } else {
            preSelected = products.value.map((product: ProductObj) => ({
                id: product?.id,
                variants: product?.variants.map((variant: VariantsObj) => ({
                    id: variant?.id,
                })),
            }));
        }

        let selected: any = await shopify.resourcePicker({
            type: "product",
            multiple: multiple,
            query: initialQuery,
            action: "select",
            selectionIds: preSelected,
            filter: {
                query: "status:active AND published_status:published",
                variants: showVariantPicker,
            },
        });

        if (selected) {
            const productsArr: ProductObj[] = [];

            if (excludeProducts.length > 0) {
                selected = selected.filter((temp: ProductObj) => {
                    const isExcluded = excludeProducts.some((excluded) => excluded.id === temp.id);
                    if (isExcluded) {
                        shopify.toast.show("The main product cannot be added to the offered product list.", {
                            isError: true,
                        });
                        return false;
                    }
                    return true;
                });
            }

            if (bundleLevel == "product") {
                selected?.flatMap((product: any) => {
                    const existingProduct = products.value.find((p: any) => p.id === product.id);
                    productsArr.push({
                        id: product?.id,
                        images: product?.images,
                        title: product?.title,
                        handle: product?.handle,
                        quantity: 1,
                        maxProductStock: product?.totalInventory,
                        selectedVariants: [],
                        subscriptions: [],
                        oneTimePurchase: true,
                        isSubscribed: false,
                        alternateProducts: existingProduct?.alternateProducts || [],
                        variants: product?.variants?.map((variant: VariantsObj) => ({
                            image: { ...variant.image },
                            id: variant?.id,
                            displayName: variant.displayName,

                            title: variant?.title?.toLowerCase() == "default title" ? product?.title : variant?.title,
                            price: variant?.price,
                            quantity: 1,
                            selectedOptions: variant?.selectedOptions,
                            copyPrice: variant.price,
                            compareAtPrice: variant.compareAtPrice ? variant.compareAtPrice : variant.price,
                        })),
                        options: product?.options,
                    });
                });
            } else {
                selected?.flatMap((product: any) => {
                    product?.variants.forEach((variant: VariantsObj) => {
                        const existingProduct = products.value.find(
                            (p: any) => p.id === product.id && p.variants[0]?.id === variant.id,
                        );
                        productsArr.push({
                            id: product?.id,
                            images: [variant.image || product.images[0]],
                            title:
                                bundleType === BundleTypes.STANDARD
                                    ? product?.title
                                    : variant?.title?.toLowerCase() === "default title"
                                      ? product?.title
                                      : variant?.title,
                            handle: product.handle,
                            quantity: 1,
                            maxProductStock: product?.totalInventory,
                            selectedVariants: [],
                            subscriptions: [],
                            oneTimePurchase: true,
                            isSubscribed: false,
                            alternateProducts: existingProduct?.alternateProducts || [],
                            variants: [
                                {
                                    id: variant?.id,
                                    displayName: variant.displayName,
                                    title:
                                        variant?.title?.toLowerCase() == "default title"
                                            ? product?.title
                                            : variant?.title,
                                    price: variant.price,
                                    image: {},
                                    selectedOptions: variant.selectedOptions,
                                    copyPrice: variant.price,
                                    compareAtPrice: variant.compareAtPrice ? variant.compareAtPrice : variant.price,
                                },
                            ],
                            options: product?.options,
                        });
                    });
                });
            }

            try {
                setIsFetchingRequest(true);

                const newProductsArr = await getProductsDynamicallyAndReturnNewProductsArray(
                    productsArr,
                    sft,
                    false,
                    bundleType,
                );
                if (MNMSectional) {
                    dispatchBundleConfig({
                        payload: newProductsArr,
                        key: `mix_match_sections`,
                        index: index,
                        subKey: updateKey,
                        isArray: true,
                    });
                } else {
                    dispatchBundleConfig({
                        payload: newProductsArr,
                        key: [updateKey],
                    });
                }
                products.onChange(newProductsArr);
                setIsFetchingRequest(false);
            } catch (error) {
                console._log(error);
                setIsFetchingRequest(false);

                if (MNMSectional) {
                    dispatchBundleConfig({
                        payload: productsArr,
                        key: `mix_match_sections`,
                        index: index,
                        subKey: updateKey,
                        isArray: true,
                    });
                } else {
                    dispatchBundleConfig({
                        payload: productsArr,
                        key: [updateKey],
                    });
                }
                products.onChange(productsArr);
            }
        }
    };

    const [error, setError] = useState<string>("");

    const handleAlternateProduct = async (productId: string) => {
        const mainProduct = products.value.find((p: any) => p.id === productId);

        const selected = await shopify.resourcePicker({
            type: "product",
            multiple: false,
            query: "",
            action: "select",
            filter: {
                query: "status:active AND published_status:published",
            },
        });

        if (!selected || selected.length === 0) return;

        const alternate = selected[0];

        if (mainProduct.alternateProducts?.some((p: any) => p.id === alternate.id) || alternate.id === mainProduct.id) {
            setError("This product is already added as an alternate or is the same as the main product.");
            return;
        }

        const updatedProducts = products.value.map((p: ProductObj) => {
            if (p.id === productId) {
                return {
                    ...p,
                    alternateProducts: [...(p.alternateProducts || []), alternate],
                };
            }
            return p;
        });

        dispatchBundleConfig({
            payload: updatedProducts,
            key: [updateKey],
        });
        products.onChange(updatedProducts);
        setError("");
    };

    const handleRemoveAlternate = (mainId: string, altId: string) => {
        const temp = [...products.value];
        const mainProductIndex = temp.findIndex((p: any) => p.id === mainId);
        if (mainProductIndex !== -1) {
            const mainProduct = { ...temp[mainProductIndex] };
            const currentAlternates = Array.isArray(mainProduct.alternateProducts)
                ? [...mainProduct.alternateProducts]
                : [];
            const newAlternates = currentAlternates.filter((a: any) => a.id !== altId);
            mainProduct.alternateProducts = newAlternates;
            temp[mainProductIndex] = mainProduct;
            dispatchBundleConfig({
                payload: temp,
                key: [updateKey],
            });
            products.onChange(temp);
        }
    };

    const onDropReferences = (res: any) => {
        if (!res?.destination?.droppableId || !res?.source?.droppableId) return;
        else {
            moveCard(res.source.index, res.destination.index);
        }
    };

    const getLabel = () => {
        if (bundleLevel === "product") {
            if (bundleType === "product_add_on") {
                return products_type === "main_product"
                    ? `Add Specific Product${multiple === 1 ? "" : "s"}`
                    : `Add Add-on Product${multiple === 1 ? "" : "s"}`;
            }

            return renderArr.length === multiple
                ? `Edit Product${multiple === 1 ? "" : "s"}`
                : `+ Add Product${multiple === 1 ? "" : "s"}`;
        }

        return "+ Add Variants";
    };

    // eslint-disable-next-line
    const quantityError = useCallback((qty = 0, max = 0) => {
        // console._log(max);

        if (qty <= 0) {
            return "Minimum 1";
        } else {
            return "";
        }
    }, []);
    // console._log("isMobile", isMobile);

    return (
        <DragDropContext onDragEnd={onDropReferences}>
            <Droppable droppableId="droppable">
                {(provided) => (
                    <div className={styles.headingsDiv} {...provided.droppableProps} ref={provided.innerRef}>
                        <div className={styles.productNameWrapper}>
                            {bundleType !== "mixmatch" && (
                                <div className={styles.productName} style={{ width: "max-content" }}>
                                    {`${customTitle || "Included"} ${
                                        bundleLevel.charAt(0).toUpperCase() + bundleLevel.slice(1)
                                    }${renderArr?.length > 1 ? "s" : ""}`}{" "}
                                </div>
                            )}

                            {!!products?.value?.length && (
                                <Button onClick={() => handleProductSearch()} variant={"secondary"}>
                                    {getLabel()}
                                </Button>
                            )}
                        </div>
                        {renderArr?.length ? (
                            <div className={styles.productsRender}>
                                {renderArr.map((product: any, pIdx: number) => {
                                    const showVariantName =
                                        product?.variants?.length === 1 &&
                                        product?.variants[0]?.title?.toLowerCase() !== product?.title?.toLowerCase()
                                            ? product?.variants[0]?.title
                                            : "";

                                    return (
                                        <Draggable
                                            key={product?.variants[0]?.id}
                                            draggableId={product?.variants[0]?.id + ""}
                                            index={pIdx}
                                        >
                                            {(provided, snapshot) => (
                                                <div
                                                    key={product?.variants[0]?.id}
                                                    className={showAlternateProducts ? styles.productWithConnector : ""}
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "flex-start",
                                                        alignItems: "center",
                                                        gap: "10px",
                                                        ...provided.draggableProps.style,
                                                        borderRadius: 10,
                                                        border: snapshot.isDragging
                                                            ? "1px solid black"
                                                            : "1px solid transparent",
                                                        backgroundColor: "white",
                                                        padding: "5px",
                                                    }}
                                                >
                                                    <div {...provided.dragHandleProps}>
                                                        <DragHandle />
                                                    </div>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            width: "100%",
                                                            cursor: "default",
                                                        }}
                                                    >
                                                        <div className={styles.productMeta}>
                                                            <div className={styles.productName}>
                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                        justifyContent: "center",
                                                                        alignItems: "center",
                                                                        overflow: "hidden",
                                                                        border: `1px solid var(--p-color-border)`,
                                                                        cursor: "pointer",
                                                                        maxWidth: 55,
                                                                        maxHeight: 55,
                                                                        minWidth: 55,
                                                                        minHeight: 55,
                                                                        background: "var(--p-color-bg-surface)",
                                                                        borderRadius: "var(--p-border-radius-200)",
                                                                    }}
                                                                >
                                                                    <img
                                                                        alt={product?.images[0]?.altText}
                                                                        src={product?.images[0]?.originalSrc}
                                                                        style={{
                                                                            width: "100%",
                                                                            height: "100%",
                                                                            padding: 2,
                                                                            objectFit: "cover",
                                                                            borderRadius: 10,
                                                                            objectPosition: "center",
                                                                        }}
                                                                    />
                                                                </div>
                                                                <div className={styles.productTitleWrapper}>
                                                                    <div
                                                                        style={{
                                                                            whiteSpace: "normal",
                                                                            cursor: "pointer",
                                                                        }}
                                                                        onClick={() => {
                                                                            if (shopify.config.shop) {
                                                                                window.open(
                                                                                    `https://${shopify.config.shop}/products/${product.handle}`,
                                                                                    "_blank",
                                                                                );
                                                                            }
                                                                        }}
                                                                        className={styles.productTitle}
                                                                    >
                                                                        {product?.title}
                                                                    </div>

                                                                    <div
                                                                        style={{
                                                                            fontSize: 11,
                                                                        }}
                                                                    >
                                                                        {product?.variants?.length > 1 ? (
                                                                            <div>
                                                                                <span
                                                                                    className={styles.variantsLabel}
                                                                                    onClick={() => {
                                                                                        handleProductSearch(
                                                                                            product?.title,
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    {`${product?.variants?.length}
                                                                            variants
                                                                            `}
                                                                                </span>
                                                                                included
                                                                            </div>
                                                                        ) : (
                                                                            ``
                                                                        )}
                                                                        {bundleLevel === "variant" &&
                                                                            bundleType === BundleTypes.STANDARD && (
                                                                                <div
                                                                                    className={
                                                                                        styles.variantInfoContainer
                                                                                    }
                                                                                >
                                                                                    <RenderVariantInfo
                                                                                        product={
                                                                                            showVariantName
                                                                                                ? {
                                                                                                      ...product,
                                                                                                      title: showVariantName,
                                                                                                  }
                                                                                                : product
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                    </div>

                                                                    {bundleType != BundleTypes.VOLUME_DISCOUNT &&
                                                                        !product?.subscriptions?.length &&
                                                                        isFetchingRequest && (
                                                                            <SkeletonBodyText lines={1} />
                                                                        )}

                                                                    <div
                                                                        style={{
                                                                            fontSize: 11,
                                                                        }}
                                                                    >
                                                                        <ShowSubscriptions
                                                                            product={product}
                                                                            products={products}
                                                                            dispatchBundleConfig={dispatchBundleConfig}
                                                                            renderArr={renderArr}
                                                                            updateKey={updateKey}
                                                                            MNMSectional={MNMSectional}
                                                                            index={index}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div
                                                                className={styles.productQty}
                                                                style={{
                                                                    marginTop: -25,
                                                                }}
                                                            >
                                                                {[
                                                                    BundleTypes.BXGY,
                                                                    BundleTypes.STANDARD,
                                                                    BundleTypes.PRODUCT_ADDONS,
                                                                ].includes(bundleType as any) &&
                                                                    !hideQuantity && (
                                                                        <div
                                                                            style={{
                                                                                minWidth: 90,
                                                                                maxWidth: 90,
                                                                            }}
                                                                        >
                                                                            <TextField
                                                                                label={
                                                                                    <div
                                                                                        style={{
                                                                                            fontSize: 12,
                                                                                            fontWeight: 450,
                                                                                            marginBottom: -3,
                                                                                        }}
                                                                                    >
                                                                                        Quantity
                                                                                    </div>
                                                                                }
                                                                                type={"number"}
                                                                                min={1}
                                                                                autoComplete={"off"}
                                                                                onChange={(val) => {
                                                                                    const regex = /^\d*$/;
                                                                                    if (!regex.test(val)) {
                                                                                        return;
                                                                                    }

                                                                                    const quantity = +val;

                                                                                    handleQuantity(
                                                                                        product?.id,
                                                                                        product.variants[0]?.id,
                                                                                        quantity,
                                                                                    );
                                                                                }}
                                                                                size={"slim"}
                                                                                value={
                                                                                    (+product.quantity).toString() as unknown as string
                                                                                }
                                                                                onBlur={() => {
                                                                                    if (+product.quantity <= 0) {
                                                                                        handleQuantity(
                                                                                            product?.id,
                                                                                            product.variants[0]?.id,
                                                                                            1,
                                                                                        );
                                                                                    }
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                <div
                                                                    onClick={() => {
                                                                        removeProduct(
                                                                            product?.id,
                                                                            product?.variants[0]?.id,
                                                                        );
                                                                    }}
                                                                    style={{
                                                                        cursor: "pointer",
                                                                        display: "flex",
                                                                        justifyContent: "center",
                                                                        alignItems: "center",
                                                                        marginTop: "22px",
                                                                    }}
                                                                >
                                                                    <CloseIcon />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className={styles.variantsWrapper}></div>
                                                        {showAlternateProducts && (
                                                            <div className={styles.conditionWrapper}>
                                                                <p className={styles.paragraph}>
                                                                    If product is in the order?
                                                                </p>

                                                                {product?.alternateProducts?.map((alt: any) => (
                                                                    <div key={alt.id} className={styles.upperAltDiv}>
                                                                        <div className={styles.singleProductAlt}>
                                                                            <div className={styles.singleProductLeft}>
                                                                                <Thumbnail
                                                                                    source={
                                                                                        alt.images[0]?.originalSrc ||
                                                                                        ProductIcon
                                                                                    }
                                                                                    alt={alt.images[0]?.altText}
                                                                                    size="extraSmall"
                                                                                />

                                                                                <span className={styles.altTitle}>
                                                                                    {alt.title}
                                                                                </span>
                                                                            </div>
                                                                            <div
                                                                                className={styles.singleProductRight}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleRemoveAlternate(
                                                                                        product.id,
                                                                                        alt.id,
                                                                                    );
                                                                                }}
                                                                            >
                                                                                <XIcon
                                                                                    height={12}
                                                                                    width={12}
                                                                                    fill="#616161"
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}

                                                                {(product.alternateProducts?.length ?? 0) < 1 && (
                                                                    <div
                                                                        style={{
                                                                            width: "170px",
                                                                        }}
                                                                    >
                                                                        <Button
                                                                            size="micro"
                                                                            icon={PlusIcon}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleAlternateProduct(product.id);
                                                                            }}
                                                                        >
                                                                            Show another product
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </Draggable>
                                    );
                                })}
                                {error && (
                                    <div style={{ margin: "10px 0" }}>
                                        <InlineError message={error} fieldID="alternateProduct" />
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className={styles.emptyProductBox} onClick={() => handleProductSearch()}>
                                <Button variant={"secondary"} loading={isFetchingRequest}>
                                    {getLabel()}
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    );
};

export default RenderSelectedProduct;

const ShowSubscriptions = ({
    product,
    dispatchBundleConfig,
    renderArr,
    products,
    updateKey,
    MNMSectional,
    index,
}: {
    product: any;
    dispatchBundleConfig: any;
    renderArr: any;
    products: any;
    updateKey: any;
    MNMSectional?: boolean;
    index?: number;
}) => {
    const [openSubscriptionModal, setOpenSubscriptionModal] = useState(false);

    return (
        <>
            {product?.subscriptions?.length > 0 ? (
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                    }}
                >
                    <p className={styles.variantsLabel} onClick={() => setOpenSubscriptionModal(true)}>
                        {product?.subscriptions?.length} subscriptions
                    </p>
                    &nbsp;included
                </div>
            ) : (
                ""
            )}

            {openSubscriptionModal && (
                <SubscriptionModal
                    productSub={product}
                    setOpenSubscriptionModal={setOpenSubscriptionModal}
                    dispatchBundleConfig={dispatchBundleConfig}
                    renderArr={renderArr}
                    products={products}
                    updateKey={updateKey}
                    MNMSectional={MNMSectional}
                    index={index}
                />
            )}
        </>
    );
};

const SubscriptionModal = ({
    productSub,
    setOpenSubscriptionModal,
    dispatchBundleConfig,
    renderArr,
    products,
    updateKey,
    MNMSectional,
    index,
}: {
    productSub: any;
    setOpenSubscriptionModal: any;
    dispatchBundleConfig: any;
    renderArr: any;
    products: any;
    updateKey: any;
    MNMSectional?: boolean;
    index?: number;
}) => {
    // useEffect(() => {
    //     /**
    //     Conditions ->
    //     If one time purchase is true and all subs are false, then show left most design without subscribe button
    //     If one time purchase is true and atleast one sub is true then show left most design with subscribe button
    //     If one time purchase is false and atleast one sub is true, then show middle design
    //     If one time purchase is false and all subs are false, then make one time purchase true by default
    //     One time purchase check is needed because, what if user wanna sell only the subscription model
    //     */
    //     const realProduct = renderArr.find(
    //         (prod: any) => prod.id == productSub.id,
    //     );

    //     const areAllUnchecked = productSub.subscriptions.every(
    //         (p: any) => p.checked === false,
    //     );

    //     if (areAllUnchecked) {
    //         realProduct.isSubscribed = false;
    //         realProduct.oneTimePurchase = true;
    //         dispatchBundleConfig({
    //             payload: renderArr,
    //             key: [updateKey],
    //         });
    //         products.onChange(renderArr);
    //     }
    // }, [productSub, products.value]);

    return (
        <CustomModal
            noScroll
            open={true}
            title="Select Product Subscriptions"
            onClose={() => setOpenSubscriptionModal(false)}
            primaryAction={{
                content: "Alright",
                plain: true,
                onAction: () => setOpenSubscriptionModal(false),
            }}
            noClose={true}
        >
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    justifyContent: "center",
                    alignItems: "flex-start",
                }}
            >
                {productSub.subscriptions.map((plan: any) => (
                    <div key={plan.id}>
                        <Checkbox
                            label={plan.name}
                            value=""
                            checked={plan.checked}
                            onChange={(val) => {
                                const temp: ProductObj[] = [...renderArr];
                                const idx: number | undefined = temp.findIndex((p) => p.id == productSub.id);

                                if (idx >= 0) {
                                    const realProduct = temp[idx];

                                    const sub = realProduct?.subscriptions.find((s: any) => s.id == plan.id);

                                    if (sub) {
                                        sub.checked = val;
                                    }

                                    const areAllUnchecked = realProduct.subscriptions.every(
                                        (p: any) => p.checked === false,
                                    );
                                    if (areAllUnchecked) {
                                        realProduct.isSubscribed = false;
                                        realProduct.oneTimePurchase = true;
                                    }

                                    temp[idx] = { ...realProduct };

                                    if (MNMSectional) {
                                        dispatchBundleConfig({
                                            payload: temp,
                                            key: `mix_match_sections`,
                                            index: index,
                                            subKey: updateKey,
                                            isArray: true,
                                        });
                                    } else {
                                        dispatchBundleConfig({
                                            payload: temp,
                                            key: [updateKey],
                                        });
                                    }

                                    products.onChange(temp);
                                }

                                // const realProduct = renderArr.find(
                                //     (prod: any) => prod.id == productSub.id,
                                // );
                                // const subs = realProduct.subscriptions.find(
                                //     (s: any) => s.id == plan.id,
                                // );
                                // subs.checked = val;

                                // dispatchBundleConfig({
                                //     payload: renderArr,
                                //     key: [updateKey],
                                // });
                                // products.onChange(renderArr);
                            }}
                        />
                    </div>
                ))}

                <p>If you do not select any of the above subscriptions, then this value gets checked</p>
                <Checkbox
                    label={"One time purchase"}
                    value=""
                    checked={productSub.oneTimePurchase}
                    onChange={(val) => {
                        const temp: ProductObj[] = [...renderArr];
                        const idx: number | undefined = temp.findIndex((p) => p.id == productSub.id);

                        if (idx >= 0) {
                            const realProduct = temp[idx];

                            if (realProduct) {
                                const areAllUnchecked = productSub.subscriptions.every((p: any) => p.checked === false);

                                if (areAllUnchecked) {
                                    realProduct.isSubscribed = false;
                                    realProduct.oneTimePurchase = true;

                                    if (MNMSectional) {
                                        dispatchBundleConfig({
                                            payload: renderArr,
                                            key: `mix_match_sections`,
                                            index: index,
                                            subKey: updateKey,
                                            isArray: true,
                                        });
                                    } else {
                                        dispatchBundleConfig({
                                            payload: renderArr,
                                            key: [updateKey],
                                        });
                                    }
                                    products.onChange(renderArr);
                                } else {
                                    realProduct.oneTimePurchase = val;
                                    realProduct.isSubscribed = false;
                                }
                            }

                            temp[idx] = { ...realProduct };

                            if (MNMSectional) {
                                dispatchBundleConfig({
                                    payload: temp,
                                    key: `mix_match_sections`,
                                    index: index,
                                    subKey: updateKey,
                                    isArray: true,
                                });
                            } else {
                                dispatchBundleConfig({
                                    payload: temp,
                                    key: [updateKey],
                                });
                            }

                            products.onChange(temp);
                        }

                        // const realProduct = renderArr.find(
                        //     (prod: any) => prod.id == productSub.id,
                        // );

                        // realProduct.oneTimePurchase = val;
                        // dispatchBundleConfig({
                        //     payload: renderArr,
                        //     key: [updateKey],
                        // });
                        // products.onChange(renderArr);
                    }}
                />
            </div>
        </CustomModal>
    );
};
